import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  ClipboardCheck, AlertTriangle, Clock, Calendar, BookOpen, CheckCircle2,
  XCircle, Loader2, Banknote,
} from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { API, formatApiErrorDetail } from "../context/AuthContext";
import { useFmtXof } from "../lib/i18nFmt";

export default function SessionsList({ mode = "parent", onChange }) {
  const { t } = useTranslation();
  const fmt = useFmtXof();
  const statusBadge = (s) => {
    if (s === "CONFIRMED") return { cls: "bg-[#F0FDF4] text-[#15803D] border-[#16A34A]/30", label: t("sessions.statusConfirmed") };
    if (s === "SUBMITTED") return { cls: "bg-yellow-50 text-yellow-700 border-yellow-200", label: t("sessions.statusSubmitted") };
    if (s === "DISPUTED") return { cls: "bg-red-50 text-red-700 border-red-200", label: t("sessions.statusDisputed") };
    if (s === "REJECTED") return { cls: "bg-gray-100 text-gray-700 border-gray-300", label: t("sessions.statusRejected") };
    return { cls: "bg-gray-50 text-gray-700 border-gray-200", label: s };
  };

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeId, setDisputeId] = useState(null);
  const [disputeReason, setDisputeReason] = useState("");
  const [actioning, setActioning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = mode === "admin" ? `${API}/admin/sessions` : `${API}/sessions/mine`;
      const { data } = await axios.get(url);
      setItems(data.items || []);
      setSummary(data.summary || data.kpis || null);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => { load(); }, [load]);

  const confirm = async (id) => {
    setActioning(true);
    try {
      await axios.post(`${API}/sessions/${id}/confirm`);
      toast.success(t("sessions.toastValidated"));
      await load();
      onChange?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setActioning(false); }
  };

  const openDispute = (id) => { setDisputeId(id); setDisputeReason(""); setDisputeOpen(true); };

  const submitDispute = async () => {
    if (disputeReason.trim().length < 10) return toast.error(t("sessions.disputeMin"));
    setActioning(true);
    try {
      await axios.post(`${API}/sessions/${disputeId}/dispute`, { reason: disputeReason.trim() });
      toast.success(t("sessions.toastDisputed"));
      setDisputeOpen(false);
      await load();
      onChange?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setActioning(false); }
  };

  const resolveAdmin = async (id, decision) => {
    const note = prompt(t("sessions.resolutionPrompt"), "") || "";
    setActioning(true);
    try {
      await axios.post(`${API}/admin/sessions/${id}/resolve`, { decision, note });
      toast.success(decision === "CONFIRMED" ? t("sessions.toastAdminConfirmed") : t("sessions.toastAdminRejected"));
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setActioning(false); }
  };

  if (loading) {
    return (
      <Card className="p-6 border-[#E5EBE7] flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-[#16A34A]" />
      </Card>
    );
  }

  return (
    <div data-testid={`sessions-list-${mode}`}>
      {summary && mode === "parent" && (
        <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
          <Badge className="bg-yellow-50 text-yellow-700 border-yellow-200 border">{t("sessions.kpiToConfirm", { count: summary.to_confirm_count })}</Badge>
          {summary.disputed_count > 0 && (
            <Badge className="bg-red-50 text-red-700 border-red-200 border">{t("sessions.kpiDisputes", { count: summary.disputed_count })}</Badge>
          )}
        </div>
      )}
      {summary && mode === "educator" && (
        <div className="mb-4 grid sm:grid-cols-3 gap-3 text-sm">
          <Card className="p-4 border-[#E5EBE7]">
            <div className="text-xs text-[#4B5563] uppercase tracking-wide">{t("sessions.kpiConfirmedHours")}</div>
            <div className="font-display text-2xl font-bold text-[#0A2518] mt-1" data-testid="educator-confirmed-hours">{summary.confirmed_hours} h</div>
          </Card>
          <Card className="p-4 border-[#E5EBE7]">
            <div className="text-xs text-[#4B5563] uppercase tracking-wide">{t("sessions.kpiMonthNet")}</div>
            <div className="font-display text-2xl font-bold text-[#15803D] mt-1" data-testid="educator-confirmed-net">{fmt(summary.confirmed_net_xof)}</div>
          </Card>
          <Card className="p-4 border-[#E5EBE7]">
            <div className="text-xs text-[#4B5563] uppercase tracking-wide">{t("sessions.kpiPendingParent")}</div>
            <div className="font-display text-2xl font-bold text-yellow-700 mt-1">{summary.submitted_pending_count}</div>
          </Card>
        </div>
      )}
      {summary && mode === "admin" && (
        <div className="mb-4 grid sm:grid-cols-4 gap-3 text-sm">
          <Card className="p-4 border-[#E5EBE7]"><div className="text-xs text-[#4B5563] uppercase">{t("sessions.adminToConfirm")}</div><div className="font-display text-2xl font-bold text-[#0A2518]">{summary.submitted_count}</div></Card>
          <Card className="p-4 border-[#E5EBE7]"><div className="text-xs text-[#4B5563] uppercase">{t("sessions.adminConfirmed")}</div><div className="font-display text-2xl font-bold text-[#15803D]">{summary.confirmed_count}</div></Card>
          <Card className="p-4 border-[#E5EBE7]"><div className="text-xs text-[#4B5563] uppercase">{t("sessions.adminDisputed")}</div><div className="font-display text-2xl font-bold text-red-700" data-testid="admin-disputed-count">{summary.disputed_count}</div></Card>
          <Card className="p-4 border-[#E5EBE7]"><div className="text-xs text-[#4B5563] uppercase">{t("sessions.adminCommission")}</div><div className="font-display text-2xl font-bold text-[#15803D]">{fmt(summary.confirmed_fee_xof)}</div></Card>
        </div>
      )}

      {items.length === 0 ? (
        <Card className="p-6 border-[#E5EBE7] text-center text-[#4B5563]">
          {mode === "parent" ? t("sessions.emptyParent") : t("sessions.empty")}
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((s) => {
            const sb = statusBadge(s.status);
            return (
              <Card key={s.id} className="p-5 border-[#E5EBE7]" data-testid={`session-row-${s.id}`}>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <Badge className={`${sb.cls} border`} data-testid={`session-status-${s.id}`}>{sb.label}</Badge>
                      <span className="text-xs text-[#4B5563] flex items-center gap-1"><Calendar className="h-3 w-3" /> {s.date}</span>
                      <span className="text-xs text-[#4B5563] flex items-center gap-1"><Clock className="h-3 w-3" /> {s.start_time}–{s.end_time} ({s.hours} h)</span>
                    </div>
                    <div className="font-medium text-[#0A2518] flex items-center gap-1.5"><BookOpen className="h-4 w-4 text-[#16A34A]" /> {s.subject}</div>
                    <div className="text-xs text-[#4B5563] mt-0.5">
                      {mode === "parent" ? t("sessions.educatorLabel", { name: s.educator_name }) : t("sessions.parentLabel", { name: s.parent_name })}
                    </div>
                    <p className="mt-2 text-sm text-[#0A2518]">{s.content}</p>
                    {s.dispute_reason && (
                      <div className="mt-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                        <strong>{t("sessions.disputeReasonLbl")}</strong>{s.dispute_reason}
                      </div>
                    )}
                    {s.resolution_note && (
                      <div className="mt-2 rounded-lg bg-[#F0FDF4] border border-[#16A34A]/30 px-3 py-2 text-xs text-[#15803D]">
                        <strong>{t("sessions.resolutionLbl")}</strong>{s.resolution_note}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 justify-end text-xs text-[#4B5563]"><Banknote className="h-3 w-3 text-[#16A34A]" /> {fmt(s.amount_xof)}</div>
                    {mode === "educator" && (
                      <div className="text-xs text-[#15803D] font-semibold mt-1">{t("sessions.net", { amount: fmt(s.educator_net_xof) })}</div>
                    )}
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2 justify-end">
                  {mode === "parent" && s.status === "SUBMITTED" && (
                    <>
                      <Button onClick={() => openDispute(s.id)} variant="outline" size="sm" disabled={actioning} className="rounded-full border-red-200 text-red-700 hover:bg-red-50" data-testid={`dispute-btn-${s.id}`}>
                        <AlertTriangle className="h-4 w-4 mr-1.5" /> {t("sessions.reportProblem")}
                      </Button>
                      <Button onClick={() => confirm(s.id)} size="sm" disabled={actioning} className="rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white" data-testid={`confirm-btn-${s.id}`}>
                        <ClipboardCheck className="h-4 w-4 mr-1.5" /> {t("sessions.validate")}
                      </Button>
                    </>
                  )}
                  {mode === "admin" && s.status === "DISPUTED" && (
                    <>
                      <Button onClick={() => resolveAdmin(s.id, "REJECTED")} variant="outline" size="sm" disabled={actioning} className="rounded-full border-red-200 text-red-700" data-testid={`admin-reject-${s.id}`}>
                        <XCircle className="h-4 w-4 mr-1.5" /> {t("sessions.adminReject")}
                      </Button>
                      <Button onClick={() => resolveAdmin(s.id, "CONFIRMED")} size="sm" disabled={actioning} className="rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white" data-testid={`admin-confirm-${s.id}`}>
                        <CheckCircle2 className="h-4 w-4 mr-1.5" /> {t("sessions.adminConfirm")}
                      </Button>
                    </>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={disputeOpen} onOpenChange={setDisputeOpen}>
        <DialogContent className="bg-white" data-testid="dispute-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#0A2518]">{t("sessions.disputeTitle")}</DialogTitle>
            <DialogDescription>{t("sessions.disputeDesc")}</DialogDescription>
          </DialogHeader>
          <Textarea
            value={disputeReason}
            onChange={(e) => setDisputeReason(e.target.value)}
            placeholder={t("sessions.disputePh")}
            className="min-h-[120px]"
            data-testid="dispute-reason-input"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDisputeOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={submitDispute} disabled={actioning || disputeReason.trim().length < 10} className="rounded-full bg-red-600 hover:bg-red-700 text-white" data-testid="dispute-submit-btn">
              {actioning ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("sessions.disputeSending")}</>) : (<>{t("sessions.disputeSubmit")}</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
